// Every route in the app and which nav entry it highlights. Kept free of React
// and @olytic/foundation so it can be unit tested (navigation.test.ts).

export const HOME_ROUTE = '/';
export const REPORTS_ROUTE = '/reports';
/** A saved-report folder shown as tiles (reports/ReportDashboardPage.tsx); /reports/dashboard/:folderId is one folder. */
export const REPORT_DASHBOARD_ROUTE = `${REPORTS_ROUTE}/dashboard`;
/** A folder's dashboard. */
export const reportDashboardHref = (folderId: string): string => `${REPORT_DASHBOARD_ROUTE}/${encodeURIComponent(folderId)}`;
/** My Dashboard: the signed-in person's favorite reports as tiles (MOTHER-293). Folder ids are UUIDs, so none is `favorites`. */
export const MY_DASHBOARD_ROUTE = `${REPORT_DASHBOARD_ROUTE}/favorites`;
export const SETUP_ROUTE = '/setup';
/** The Delivery app's resource planner (resourcing/ResourcingPage.tsx). */
export const RESOURCING_ROUTE = '/resourcing';
/** Seller Home, the Sales app's home for one seller: their follow-ups, recent touches and the open leads (home/SellerHomePage.tsx). */
export const SELLER_HOME_ROUTE = '/seller';
/** My Delivery, the Delivery app's home: the signed-in consultant's month (delivery/DeliveryHomePage.tsx). */
export const DELIVERY_HOME_ROUTE = '/delivery';
/** The Delivery app's work items, board and timesheet (work/WorkPage.tsx). */
export const WORK_ROUTE = '/work';
/** The Library: Foundation's building blocks (library/LibraryPage.tsx). /library/ itself is the hosted showcase. */
export const LIBRARY_ROUTE = '/building-blocks';
/** The new-product flow (products/NewProductPage.tsx): a product is created complete, or as a draft. */
export const NEW_PRODUCT_ROUTE = '/products/new';
/** The Products app's whole catalog as a tree (products/ProductCatalogPage.tsx). */
export const PRODUCT_CATALOG_ROUTE = '/product-catalog';

export const SETUP_ROUTES = {
  home: SETUP_ROUTE,
  users: `${SETUP_ROUTE}/users`,
  aiUsage: `${SETUP_ROUTE}/ai/usage`,
  aiCredits: `${SETUP_ROUTE}/ai/credits`,
  aiCapabilities: `${SETUP_ROUTE}/ai/capabilities`,
  objects: `${SETUP_ROUTE}/objects`,
  fieldsFromHistory: `${SETUP_ROUTE}/fields-from-history`,
  recycleBin: `${SETUP_ROUTE}/recycle-bin`,
  /** Accounts and contacts that share a name, to review and merge (setup/PossibleDuplicatesPage.tsx). */
  possibleDuplicates: `${SETUP_ROUTE}/possible-duplicates`,
  /** Partner applications (setup/PartnersPage.tsx); /setup/partners/:id is one partner. */
  partners: `${SETUP_ROUTE}/partners`,
} as const;

/** Addresses that moved into setup. Old links and bookmarks still land on the right page. */
export const MOVED_ROUTES: Readonly<Record<string, string>> = {
  '/ai/usage': SETUP_ROUTES.aiUsage,
  '/ai/credits': SETUP_ROUTES.aiCredits,
  '/ai/capabilities': SETUP_ROUTES.aiCapabilities,
};

export type ObjectKey =
  | 'leads'
  | 'accounts'
  | 'contacts'
  | 'opportunities'
  | 'audiences'
  | 'projects'
  | 'time-entries'
  | 'meetings'
  | 'activities'
  | 'documents'
  | 'products'
  | 'product-groups'
  | 'prices'
  | 'quotes'
  | 'assets'
  | 'revenue'
  | 'product-options'
  | 'price-grids'
  | 'price-grid-cells'
  | 'invoices'
  | 'invoice-lines'
  | 'invoice-runs'
  | 'billing-schedules'
  | 'billing-accounts'
  | 'billing-policies'
  | 'billing-treatments'
  | 'billing-treatment-items'
  | 'contracts'
  | 'guidelines'
  | 'resource-roles'
  | 'product-resource-roles'
  | 'people'
  | 'user-resource-roles'
  | 'resource-capacity'
  | 'resource-assignments'
  | 'opportunity-contacts'
  | 'deal-retros'
  | 'agent-conversations'
  | 'research-sources'
  | 'research-findings'
  | 'research-figures'
  | 'deliverable-types'
  | 'deliverables';

export interface ObjectEntry {
  key: ObjectKey;
  label: string;
  href: string;
}

/**
 * The ops objects. The key doubles as the route segment. Which objects a nav
 * shows, and in what order, is up to each app (tabs.tsx).
 */
export const OBJECTS: readonly ObjectEntry[] = (
  [
    ['leads', 'Leads'],
    ['accounts', 'Accounts'],
    ['contacts', 'Contacts'],
    ['opportunities', 'Opportunities'],
    ['audiences', 'Audiences'],
    ['projects', 'Projects'],
    ['time-entries', 'Time Entries'],
    ['meetings', 'Meetings'],
    ['activities', 'Activities'],
    ['documents', 'Documents'],
    ['products', 'Products'],
    ['product-groups', 'Product groups'],
    ['prices', 'Prices'],
    ['quotes', 'Quotes'],
    ['assets', 'Assets'],
    ['revenue', 'Revenue'],
    ['product-options', 'Options'],
    ['price-grids', 'Price Grids'],
    ['price-grid-cells', 'Price Grid Cells'],
    ['invoices', 'Invoices'],
    ['invoice-lines', 'Invoice lines'],
    ['invoice-runs', 'Invoice runs'],
    ['billing-schedules', 'Billing schedules'],
    ['billing-accounts', 'Billing accounts'],
    ['billing-policies', 'Billing policies'],
    ['billing-treatments', 'Billing treatments'],
    ['billing-treatment-items', 'Treatment items'],
    ['contracts', 'Contracts'],
    ['guidelines', 'Guidelines'],
    ['resource-roles', 'Resource Roles'],
    ['product-resource-roles', 'Role shares'],
    ['people', 'People'],
    ['user-resource-roles', 'Person roles'],
    ['resource-capacity', 'Resource Capacity'],
    ['resource-assignments', 'Resource Assignments'],
    ['opportunity-contacts', 'Contact roles'],
    ['deal-retros', 'Deal Retros'],
    ['agent-conversations', 'Agent Conversations'],
    ['research-sources', 'Research Sources'],
    ['research-findings', 'Research Findings'],
    ['research-figures', 'Research Figures'],
    ['deliverable-types', 'Deliverable Types'],
    ['deliverables', 'Deliverables'],
  ] as const
).map(([key, label]) => ({ key, label, href: `/${key}` }));

/**
 * Setup pages, in nav order. `group` places an entry inside a nav group; an
 * `adminOnly` page is offered to Admins only (`setupPagesFor`).
 */
export const SETUP_PAGES: readonly { key: string; label: string; href: string; group?: string; adminOnly?: boolean }[] = [
  { key: 'setup-home', label: 'Setup home', href: SETUP_ROUTES.home },
  { key: 'setup-users', label: 'Users', href: SETUP_ROUTES.users },
  { key: 'ai-usage', label: 'Usage', href: SETUP_ROUTES.aiUsage, group: 'AI management' },
  { key: 'ai-credits', label: 'Credits & limits', href: SETUP_ROUTES.aiCredits, group: 'AI management' },
  {
    key: 'ai-capabilities',
    label: 'Capabilities & models',
    href: SETUP_ROUTES.aiCapabilities,
    group: 'AI management',
  },
  { key: 'setup-objects', label: 'Objects', href: SETUP_ROUTES.objects },
  { key: 'setup-fields-from-history', label: 'Fields from history', href: SETUP_ROUTES.fieldsFromHistory },
  { key: 'setup-recycle-bin', label: 'Recycle Bin', href: SETUP_ROUTES.recycleBin },
  { key: 'setup-possible-duplicates', label: 'Possible Duplicates', href: SETUP_ROUTES.possibleDuplicates, adminOnly: true },
  { key: 'setup-partners', label: 'Partners', href: SETUP_ROUTES.partners },
];

/** The setup pages offered to someone with this role key: an `adminOnly` page only to an Admin. */
export function setupPagesFor(role: string | undefined): typeof SETUP_PAGES {
  return SETUP_PAGES.filter((page) => !page.adminOnly || role === 'admin');
}

/** Whether a path belongs to setup, which has its own navigation. */
export function isSetupPath(pathname: string): boolean {
  return pathname === SETUP_ROUTE || pathname.startsWith(`${SETUP_ROUTE}/`);
}

/** Whether `pathname` is `route` or a page beneath it, never a route that only shares its first letters. */
function within(pathname: string, route: string): boolean {
  return pathname === route || pathname.startsWith(`${route}/`);
}

/** The nav entry to highlight on a page, or '' when none matches. */
export function activeKeyFor(pathname: string): string {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (path === HOME_ROUTE) return 'sales-pulse';
  if (path === SELLER_HOME_ROUTE) return 'seller-home';
  if (within(path, REPORTS_ROUTE)) return 'reports';
  if (within(path, RESOURCING_ROUTE)) return 'resourcing';
  if (within(path, WORK_ROUTE)) return 'work';
  if (path === DELIVERY_HOME_ROUTE) return 'delivery-home';
  if (within(path, LIBRARY_ROUTE)) return 'library';
  if (path === PRODUCT_CATALOG_ROUTE) return 'product-catalog';
  const object = OBJECTS.find((o) => within(path, o.href));
  if (object) return object.key;
  if (path === SETUP_ROUTES.home) return 'setup-home';
  // Most specific first: setup home is a prefix of every other setup page.
  const setup = SETUP_PAGES.filter((p) => p.href !== SETUP_ROUTES.home).find((p) => within(path, p.href));
  return setup?.key ?? '';
}
